import React from "react";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuArrowDownUp, LuEllipsis, LuSearch } from "react-icons/lu";
import {
  activeValue,
  exportCollection,
  normalizeSearch,
} from "../collectionTools";
import { requestTableRefresh } from "../../reducers/userConfigSlice";
import "./style.css";

export const ReferenceList = ({ rows, onEdit, definition }) => {
  const {
    idKey,
    singular,
    plural,
    columns,
    exportColumns,
    searchKey,
    setSearch,
  } = definition;
  const dispatch = useDispatch();
  const search = useSelector(
    (state) => state.aplicationConfig.userConfig[searchKey] || "",
  );
  const [status, setStatus] = useState("");
  const [tabId, setTabId] = useState("all");
  const tabs = definition.tabs?.(rows) || [];
  const tab = tabs.find((item) => item.id === tabId) || tabs[0];
  const [sort, setSort] = useState({ key: "name", direction: 1 });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [menu, setMenu] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const allBox = useRef(null),
    menuRef = useRef(null),
    dialogRef = useRef(null),
    inFlight = useRef(false);
  const filtered = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            (!tab?.matches || tab.matches(row)) &&
            (!status || definition.matchesFilter(row, status)) &&
            normalizeSearch(definition.searchText(row)).includes(
              normalizeSearch(search).trim(),
            ),
        )
        .sort((a, b) => {
          const value = (row) =>
            sort.key === "name"
              ? row.name
              : columns
                  .find((column) => column.key === sort.key)
                  ?.sortValue?.(row);
          const av = value(a),
            bv = value(b);
          const result =
            typeof av === "number" && typeof bv === "number"
              ? av - bv
              : String(av ?? "").localeCompare(String(bv ?? ""), "es", {
                  sensitivity: "base",
                  numeric: true,
                });
          return result * sort.direction;
        }),
    [rows, status, search, sort, definition, columns, tab],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / 25)),
    currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * 25, currentPage * 25);
  const allSelected =
    visible.length > 0 && visible.every((row) => selected.includes(row[idKey]));
  useEffect(() => {
    setSelected([]);
    setPage(1);
    setMenu(null);
  }, [search, status, rows, tabId]);
  useEffect(() => {
    if (allBox.current)
      allBox.current.indeterminate =
        !allSelected && visible.some((row) => selected.includes(row[idKey]));
  }, [allSelected, visible, selected, idKey]);
  useEffect(() => {
    if (!menu) return;
    menuRef.current?.querySelector("button")?.focus();
    const close = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !event.target.closest?.(".catalogRowTrigger")
      )
        setMenu(null);
    };
    const dismiss = () => setMenu(null);
    document.addEventListener("pointerdown", close);
    window.addEventListener("resize", dismiss);
    document.addEventListener("scroll", dismiss, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("scroll", dismiss, true);
    };
  }, [menu]);
  useEffect(() => {
    if (!confirmation) return;
    const dialog = dialogRef.current;
    dialog?.querySelector("button")?.focus();
    const keys = (event) => {
      if (event.key === "Escape" && !inFlight.current) {
        event.preventDefault();
        setConfirmation(null);
        setError("");
        requestAnimationFrame(() =>
          document.getElementById(confirmation.returnId)?.focus(),
        );
      }
      if (event.key === "Tab") {
        const buttons = [...dialog.querySelectorAll("button:not(:disabled)")];
        if (!buttons.length) {
          event.preventDefault();
          dialog.focus();
          return;
        }
        if (event.shiftKey && document.activeElement === buttons[0]) {
          event.preventDefault();
          buttons[buttons.length - 1]?.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === buttons[buttons.length - 1]
        ) {
          event.preventDefault();
          buttons[0]?.focus();
        }
      }
    };
    dialog?.addEventListener("keydown", keys);
    return () => dialog?.removeEventListener("keydown", keys);
  }, [confirmation]);
  const closeConfirmation = () => {
    if (inFlight.current) return;
    const returnId = confirmation?.returnId;
    setConfirmation(null);
    setError("");
    requestAnimationFrame(() => document.getElementById(returnId)?.focus());
  };
  const ask = (kind, entities, returnId) => {
    setMenu(null);
    setError("");
    setConfirmation({ kind, entities, returnId });
  };
  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    dialogRef.current?.focus();
    setBusy(true);
    setError("");
    try {
      const response =
        confirmation.kind === "delete"
          ? await definition.remove(
              confirmation.entities.map((row) => row[idKey]),
            )
          : await definition.toggle(confirmation.entities[0][idKey]);
      if (response?.error !== false) throw Error();
      const returnId = confirmation.returnId;
      setConfirmation(null);
      setSelected([]);
      dispatch(requestTableRefresh());
      requestAnimationFrame(() => document.getElementById(returnId)?.focus());
    } catch {
      setError("No se pudo guardar el cambio. Vuelve a intentarlo.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const changeSort = (key) => {
    setSort((previous) => ({
      key,
      direction: previous.key === key ? -previous.direction : 1,
    }));
    setPage(1);
  };
  const toggle = (id) =>
    setSelected((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id],
    );
  const exportRows = selected.length
    ? rows.filter((row) => selected.includes(row[idKey]))
    : filtered;
  const showMenu = (event, row) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenu(
      menu?.id === row[idKey]
        ? null
        : {
            id: row[idKey],
            row,
            left: Math.max(
              8,
              Math.min(rect.right - 208, window.innerWidth - 216),
            ),
            top: Math.max(
              8,
              Math.min(rect.bottom + 4, window.innerHeight - 164),
            ),
          },
    );
  };
  const sortHeader = (label, key) => (
    <th
      scope="col"
      aria-sort={
        sort.key === key
          ? sort.direction === 1
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button type="button" onClick={() => changeSort(key)}>
        {label}
        <LuArrowDownUp aria-hidden="true" />
      </button>
    </th>
  );
  return (
    <>
      {tabs.length > 0 && (
        <div
          className="referenceListTabs"
          aria-label="Vistas del listado"
          inert={confirmation ? "" : undefined}
        >
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={tab?.id === item.id}
              onClick={() => setTabId(item.id)}
            >
              {item.label}
              <span>
                {
                  rows.filter((row) => !item.matches || item.matches(row))
                    .length
                }
              </span>
            </button>
          ))}
        </div>
      )}
      <div
        className={`catalogList ${definition.key}ReferenceList`}
        inert={confirmation ? "" : undefined}
      >
        <div className="catalogToolbar">
          <label className="visually-hidden" htmlFor={definition.filterId}>
            {definition.filterLabel}
          </label>
          <select
            id={definition.filterId}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{definition.filterLabel}</option>
            {definition.filterOptions(rows).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {definition.filterExtra}
          <label className="catalogMobileSearch">
            <LuSearch aria-hidden="true" />
            <input
              type="search"
              aria-label={`Buscar en ${plural}`}
              placeholder={`Buscar en ${plural}…`}
              value={search}
              onChange={(event) => dispatch(setSearch(event.target.value))}
            />
          </label>
          {definition.exportEnabled !== false && (
            <details className="catalogExport">
              <summary aria-label="Opciones del listado">
                <LuEllipsis aria-hidden="true" />
              </summary>
              <button
                type="button"
                disabled={!exportRows.length}
                onClick={() =>
                  exportCollection(
                    exportRows,
                    exportColumns,
                    definition.exportName,
                  )
                }
              >
                Exportar CSV
              </button>
            </details>
          )}
        </div>
        {selected.length > 0 && (
          <div className="catalogSelection" role="status">
            <span>{selected.length} seleccionados</span>
            {definition.bulkRemove !== false && (
              <button
                id="catalog-delete-selection"
                type="button"
                onClick={() =>
                  ask(
                    "delete",
                    rows.filter((row) => selected.includes(row[idKey])),
                    "catalog-delete-selection",
                  )
                }
              >
                Eliminar selección
              </button>
            )}
            <button type="button" onClick={() => setSelected([])}>
              Cancelar selección
            </button>
          </div>
        )}
        <div
          className="catalogTableScroll"
          role="region"
          aria-label={`Listado de ${plural}`}
          // Keyboard access to the horizontally scrolling table.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex="0"
        >
          <table>
            <thead>
              <tr>
                <th scope="col" className="catalogCheckCell">
                  <input
                    ref={allBox}
                    type="checkbox"
                    aria-label={definition.selectAllLabel}
                    checked={allSelected}
                    disabled={!visible.length}
                    onChange={() =>
                      setSelected((previous) =>
                        allSelected
                          ? previous.filter(
                              (id) => !visible.some((row) => row[idKey] === id),
                            )
                          : [
                              ...new Set([
                                ...previous,
                                ...visible.map((row) => row[idKey]),
                              ]),
                            ],
                      )
                    }
                  />
                </th>
                {sortHeader(definition.nameLabel, "name")}
                {columns.map((column) =>
                  column.sortValue ? (
                    <React.Fragment key={column.key}>
                      {sortHeader(column.label, column.key)}
                    </React.Fragment>
                  ) : (
                    <th key={column.key} scope="col">
                      {column.label}
                    </th>
                  ),
                )}
                <th scope="col">
                  <span className="visually-hidden">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row[idKey]}>
                  <td className="catalogCheckCell">
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${row.name}`}
                      checked={selected.includes(row[idKey])}
                      onChange={() => toggle(row[idKey])}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="catalogName"
                      title={definition.nameTitle?.(row) || row.name}
                      onClick={() => onEdit(row)}
                    >
                      {row.name}
                    </button>
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className={column.className}>
                      {column.render(row)}
                    </td>
                  ))}
                  <td className="catalogActionCell">
                    <button
                      type="button"
                      className="catalogRowTrigger"
                      id={`reference-actions-${definition.key}-${row[idKey]}`}
                      aria-label={`Acciones de ${row.name}`}
                      aria-expanded={menu?.id === row[idKey]}
                      aria-haspopup="menu"
                      onClick={(event) => showMenu(event, row)}
                    >
                      <LuEllipsis aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!visible.length && (
            <p className="catalogNoMatch" role="status">
              {`No hay ${plural} que coincidan con la búsqueda.`}
            </p>
          )}
        </div>
        <footer className="catalogFooter">
          <span>
            {filtered.length}{" "}
            {filtered.length === 1 ? "resultado" : "resultados"}
          </span>
          {pages > 1 && (
            <div>
              <button
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                type="button"
              >
                Anterior
              </button>
              <span>
                {currentPage} de {pages}
              </span>
              <button
                disabled={currentPage === pages}
                onClick={() => setPage(currentPage + 1)}
                type="button"
              >
                Siguiente
              </button>
            </div>
          )}
        </footer>
      </div>
      {menu &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            aria-label={`Acciones de ${menu.row.name}`}
            className="catalogRowMenu"
            style={{ left: menu.left, top: menu.top }}
            onKeyDown={(event) => {
              if (event.key === "Escape" || event.key === "Tab") {
                setMenu(null);
                if (event.key === "Escape") {
                  event.preventDefault();
                  document
                    .getElementById(
                      `reference-actions-${definition.key}-${menu.id}`,
                    )
                    ?.focus();
                }
              }
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const items = [...menuRef.current.querySelectorAll("button")];
                items[
                  (items.indexOf(document.activeElement) +
                    (event.key === "ArrowDown" ? 1 : items.length - 1)) %
                    items.length
                ]?.focus();
              }
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onEdit(menu.row);
                setMenu(null);
              }}
            >
              {definition.editLabel?.(menu.row) || `Editar ${singular}`}
            </button>
            {(!definition.canToggle || definition.canToggle(menu.row)) && (
              <button
                type="button"
                role="menuitem"
                onClick={() =>
                  ask(
                    "toggle",
                    [menu.row],
                    `reference-actions-${definition.key}-${menu.id}`,
                  )
                }
              >
                {activeValue(menu.row.isActive) ? "Desactivar" : "Activar"}
              </button>
            )}
            {(!definition.canRemove || definition.canRemove(menu.row)) && (
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() =>
                  ask(
                    "delete",
                    [menu.row],
                    `reference-actions-${definition.key}-${menu.id}`,
                  )
                }
              >
                Eliminar
              </button>
            )}
          </div>,
          document.body,
        )}
      {confirmation &&
        createPortal(
          <div className="catalogConfirmBackdrop">
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="catalog-confirm-title"
              tabIndex={-1}
              aria-busy={busy}
              className="catalogConfirm"
            >
              <h2 id="catalog-confirm-title">
                {confirmation.kind === "delete"
                  ? `Eliminar ${plural}`
                  : "Cambiar estado"}
              </h2>
              <p>
                {confirmation.kind === "delete"
                  ? `Se eliminarán ${confirmation.entities.length} ${plural} de la selección. Esta acción no se puede deshacer.`
                  : `¿Quieres ${activeValue(confirmation.entities[0].isActive) ? "desactivar" : "activar"} ${confirmation.entities[0].name}?`}
              </p>
              {error && <p role="alert">{error}</p>}
              <div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={closeConfirmation}
                >
                  Cancelar
                </button>
                <button type="button" disabled={busy} onClick={save}>
                  {busy ? "Guardando…" : "Confirmar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
